import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react'

export enum ArcProjectStep {
  ABOUT = 0,
  STRUCTURE = 1,
  STANDARDS_AND_SKILLS = 2,
}

export interface ArcStepperState {
  currentStep: ArcProjectStep
  projectId: string | null
  hasUnsavedChanges: boolean
  isNavigationBlocked: boolean
  visitedSteps: Set<ArcProjectStep>
  completedSteps: Set<ArcProjectStep>
  isEditMode: boolean
}

export interface ArcStepperActions {
  goToStep: (step: ArcProjectStep) => boolean
  goToNextStep: (force?: boolean) => boolean
  goToPreviousStep: () => boolean
  canNavigateToStep: (step: ArcProjectStep) => boolean
  setProjectId: (id: string | null) => void
  setHasUnsavedChanges: (hasChanges: boolean) => void
  setNavigationBlocked: (isBlocked: boolean) => void
  markStepCompleted: (step: ArcProjectStep) => void
  resetStepper: () => void
  getStepLabel: (step: ArcProjectStep) => string
  getProgressPercentage: () => number
}

export type ArcStepperContextType = ArcStepperState & ArcStepperActions

const ArcStepperContext = createContext<ArcStepperContextType | undefined>(undefined)

interface ArcStepperProviderProps {
  children: ReactNode
  initialProjectId?: string
  isEditMode?: boolean
}

const STEP_LABELS: Record<ArcProjectStep, string> = {
  [ArcProjectStep.ABOUT]: 'About',
  [ArcProjectStep.STRUCTURE]: 'Structure',
  [ArcProjectStep.STANDARDS_AND_SKILLS]: 'Standards & Skills',
}

export const ArcStepperProvider = ({
  children,
  initialProjectId,
  isEditMode = false
}: ArcStepperProviderProps) => {
  const initialStep = isEditMode ? ArcProjectStep.STRUCTURE : ArcProjectStep.ABOUT
  const initialVisitedSteps = isEditMode
    ? new Set([ArcProjectStep.ABOUT, ArcProjectStep.STRUCTURE, ArcProjectStep.STANDARDS_AND_SKILLS])
    : new Set([ArcProjectStep.ABOUT])
  const initialCompletedSteps = isEditMode
    ? new Set([ArcProjectStep.ABOUT, ArcProjectStep.STRUCTURE])
    : new Set<ArcProjectStep>()

  const [currentStep, setCurrentStep] = useState<ArcProjectStep>(initialStep)
  const [projectId, setProjectIdState] = useState<string | null>(initialProjectId || null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isNavigationBlocked, setNavigationBlocked] = useState(false)
  const [visitedSteps, setVisitedSteps] = useState<Set<ArcProjectStep>>(initialVisitedSteps)
  const [completedSteps, setCompletedSteps] = useState<Set<ArcProjectStep>>(initialCompletedSteps)

  useEffect(() => {
    if (initialProjectId && initialProjectId !== projectId) {
      setProjectIdState(initialProjectId)
    }
  }, [initialProjectId]) // eslint-disable-line react-hooks/exhaustive-deps

  const setProjectId = useCallback((id: string | null) => {
    setProjectIdState(id)
  }, [])

  const markStepCompleted = useCallback((step: ArcProjectStep) => {
    setCompletedSteps(prev => new Set([...prev, step]))
  }, [])

  const canNavigateToStep = useCallback((step: ArcProjectStep): boolean => {
    if (isNavigationBlocked) return false
    if (step === currentStep) return false

    if (isEditMode) {
      return step >= ArcProjectStep.STRUCTURE
    }

    if (step < currentStep && completedSteps.has(step)) {
      return true
    }

    if (step > currentStep) {
      for (let i = ArcProjectStep.ABOUT; i < step; i++) {
        if (!completedSteps.has(i)) {
          return false
        }
      }
      return true
    }

    return false
  }, [currentStep, isNavigationBlocked, completedSteps, isEditMode])

  const goToStep = useCallback((step: ArcProjectStep): boolean => {
    if (isEditMode && step === ArcProjectStep.ABOUT) return false
    if (!canNavigateToStep(step)) return false

    setCurrentStep(step)
    setVisitedSteps(prev => new Set([...prev, step]))
    return true
  }, [canNavigateToStep, isEditMode])

  const goToNextStep = useCallback((force: boolean = false): boolean => {
    const nextStep = currentStep + 1
    if (nextStep > ArcProjectStep.STANDARDS_AND_SKILLS) return false

    if (force) {
      setCurrentStep(nextStep as ArcProjectStep)
      setVisitedSteps(prev => new Set([...prev, nextStep]))
      return true
    }

    return goToStep(nextStep as ArcProjectStep)
  }, [currentStep, goToStep])

  const goToPreviousStep = useCallback((): boolean => {
    const previousStep = currentStep - 1
    const minStep = isEditMode ? ArcProjectStep.STRUCTURE : ArcProjectStep.ABOUT
    if (previousStep < minStep) return false

    return goToStep(previousStep as ArcProjectStep)
  }, [currentStep, goToStep, isEditMode])

  const resetStepper = useCallback(() => {
    const resetStep = isEditMode ? ArcProjectStep.STRUCTURE : ArcProjectStep.ABOUT
    const resetVisited = isEditMode
      ? new Set([ArcProjectStep.ABOUT, ArcProjectStep.STRUCTURE, ArcProjectStep.STANDARDS_AND_SKILLS])
      : new Set([ArcProjectStep.ABOUT])
    const resetCompleted = isEditMode
      ? new Set([ArcProjectStep.ABOUT, ArcProjectStep.STRUCTURE])
      : new Set<ArcProjectStep>()

    setCurrentStep(resetStep)
    setProjectId(initialProjectId || null)
    setHasUnsavedChanges(false)
    setNavigationBlocked(false)
    setVisitedSteps(resetVisited)
    setCompletedSteps(resetCompleted)
  }, [isEditMode, initialProjectId])

  const getStepLabel = useCallback((step: ArcProjectStep): string => {
    return STEP_LABELS[step] || ''
  }, [])

  const getProgressPercentage = useCallback((): number => {
    const totalSteps = Object.keys(ArcProjectStep).length / 2
    const completed = visitedSteps.size - 1
    return Math.round((completed / (totalSteps - 1)) * 100)
  }, [visitedSteps])

  const contextValue = useMemo<ArcStepperContextType>(() => ({
    currentStep,
    projectId,
    hasUnsavedChanges,
    isNavigationBlocked,
    visitedSteps,
    completedSteps,
    isEditMode,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canNavigateToStep,
    setProjectId,
    setHasUnsavedChanges,
    setNavigationBlocked,
    markStepCompleted,
    resetStepper,
    getStepLabel,
    getProgressPercentage,
  }), [
    currentStep,
    projectId,
    hasUnsavedChanges,
    isNavigationBlocked,
    visitedSteps,
    completedSteps,
    isEditMode,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canNavigateToStep,
    markStepCompleted,
    resetStepper,
    getStepLabel,
    getProgressPercentage,
  ])

  return (
    <ArcStepperContext.Provider value={contextValue}>
      {children}
    </ArcStepperContext.Provider>
  )
}

export const useArcStepper = (): ArcStepperContextType => {
  const context = useContext(ArcStepperContext)

  if (!context) {
    throw new Error('useArcStepper must be used within an ArcStepperProvider')
  }

  return context
}
